import architectureSvg from '../assets/architecture.svg'

export default function HomePage() {
  return (
    <>
      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            {/* Brand */}
            <div className="mb-8">
              <span className="text-6xl md:text-7xl font-bold text-primary-900 tracking-tight">
                Ctrl+Z
              </span>
            </div>

            {/* Problem Statement */}
            <h1 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
              Stop Drowning in Backup Management!
            </h1>

            {/* Value Prop */}
            <p className="text-xl md:text-2xl text-primary-600 mb-4 font-medium">
              Timebox your repo backups
            </p>

            {/* How it works - one line */}
            <p className="text-lg text-primary-500 mb-10">
              Choose retention — 1d, 7d, 15d, or 30d. Your backups clean themselves up.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/docs"
                className="inline-flex items-center justify-center px-8 py-3 bg-primary-900 text-white font-medium rounded-lg hover:bg-primary-800 transition-colors"
              >
                Get Started
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center px-8 py-3 border border-primary-300 text-primary-700 font-medium rounded-lg hover:bg-primary-50 transition-colors"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Diagram */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-primary-900 text-center mb-8">Architecture</h2>
          <div className="bg-white rounded-xl p-4 md:p-8 shadow-sm border border-primary-200">
            <img
              src={architectureSvg}
              alt="Ctrl+Z Architecture: GitLab CI with czworks-agent sidecar uploading to SnapBucket storage"
              className="w-full max-w-4xl mx-auto"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-primary-900 text-center mb-12">How It Works</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Step 1: Token */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 bg-primary-900 text-white text-sm font-bold rounded-full">1</span>
                <h3 className="text-lg font-semibold text-primary-900">Create a CI token</h3>
              </div>
              <CodeBlock language="text" filename="Dashboard">
{`1. Sign in at ctrlz-works.web.app
2. Go to CI Tokens → Create Token
3. Copy your token (shown once!)
4. Add to CI/CD variables as CZ_TOKEN`}
              </CodeBlock>
            </div>

            {/* Step 2: GitLab CI */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 bg-primary-900 text-white text-sm font-bold rounded-full">2</span>
                <h3 className="text-lg font-semibold text-primary-900">Add to your CI pipeline</h3>
              </div>
              <CodeBlock language="yaml" filename=".gitlab-ci.yml">
{`backup:
  stage: .post
  image: registry.gitlab.com/mallocrun/ctrlz/czworks-agent:latest
  variables:
    CZ_TOKEN: \${CZ_TOKEN}
    SNAP_TYPE: git       # or "files"
    SNAP_TTL: 7d         # 1d, 7d, 15d, or 30d
    SNAP_SOURCE: \${CI_PROJECT_DIR}
  script:
    - /app/czworks-job
  rules:
    - if: $CI_COMMIT_BRANCH == $CI_DEFAULT_BRANCH`}
              </CodeBlock>
            </div>

            {/* Step 3: Storage */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 bg-primary-900 text-white text-sm font-bold rounded-full">3</span>
                <h3 className="text-lg font-semibold text-primary-900">Backups stored automatically</h3>
              </div>
              <CodeBlock language="text" filename="Storage Structure">
{`snaps-7d/
└── {tenant}/
    └── snaps/
        ├── git_20260111T120000Z_abc123.bundle.gz
        ├── git_20260110T120000Z_def456.bundle.gz
        └── git_20260109T120000Z_ghi789.bundle.gz

# Auto-expires after retention period (1d, 7d, 15d, 30d)`}
              </CodeBlock>
            </div>

            {/* Step 4: Restore */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 bg-primary-900 text-white text-sm font-bold rounded-full">4</span>
                <h3 className="text-lg font-semibold text-primary-900">Restore when needed</h3>
              </div>
              <CodeBlock language="bash" filename="Terminal">
{`# Browse backups in the dashboard, or download directly:
curl -H "Authorization: Bearer \$API_KEY" \\
  "https://your-bucket.malloc.run/snaps-7d/.../backup.bundle.gz" \\
  -o backup.bundle.gz

# Restore git bundle
gunzip backup.bundle.gz
git clone backup.bundle restored-repo`}
              </CodeBlock>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-primary-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-primary-900 text-center mb-12">Why Ctrl+Z?</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              title="CI-Native"
              description="Runs as a sidecar in your existing GitLab CI or GitHub Actions. No new infrastructure."
            />
            <FeatureCard
              title="Secure by Design"
              description="CI tokens exchange for credentials at runtime. No secrets in your pipeline config."
            />
            <FeatureCard
              title="Set It & Forget It"
              description="Backups auto-expire after your retention window. No cleanup scripts needed."
            />
            <FeatureCard
              title="Global Edge Storage"
              description="Built on Cloudflare R2. Fast uploads from anywhere, automatic expiration."
            />
            <FeatureCard
              title="Zero Config"
              description="Works with GitLab CI variables out of the box. GitHub Actions coming soon."
            />
            <FeatureCard
              title="Simple Restore"
              description="Download any backup with a single curl command. No complex restore procedures."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">
            Ready to add backup to your CI?
          </h2>
          <p className="text-primary-600 mb-8">
            Read the docs to get started. Setup takes about 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/docs"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary-900 text-white font-medium rounded-lg hover:bg-primary-800 transition-colors"
            >
              Documentation
            </a>
            <a
              href="/swagger"
              className="inline-flex items-center justify-center px-6 py-3 border border-primary-300 text-primary-700 font-medium rounded-lg hover:bg-primary-50 transition-colors"
            >
              API Reference
            </a>
          </div>
        </div>
      </section>
    </>
  )
}

function CodeBlock({ children, language, filename }: { children: string; language: string; filename: string }) {
  return (
    <div className="rounded-lg overflow-hidden border border-primary-200">
      <div className="bg-primary-800 px-4 py-2 flex items-center justify-between">
        <span className="text-primary-300 text-sm font-mono">{filename}</span>
        <span className="text-primary-400 text-xs">{language}</span>
      </div>
      <pre className="bg-primary-900 p-4 overflow-x-auto">
        <code className="text-sm text-primary-100 font-mono leading-relaxed whitespace-pre">
          {children}
        </code>
      </pre>
    </div>
  )
}

function FeatureCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-white p-6 rounded-lg border border-primary-200">
      <h3 className="text-lg font-semibold text-primary-900 mb-2">{title}</h3>
      <p className="text-primary-600 text-sm leading-relaxed">{description}</p>
    </div>
  )
}
